import React, { useEffect, useState } from 'react';
import { UsersIcon, UserIcon, EyeIcon, CodeIcon, PlusIcon, KeyIcon, TrashIcon, RefreshCwIcon, ServerIcon } from 'lucide-react';
import { mockUserData } from '../utils/mockData';
import { mockModelData } from '../utils/mockData';
const UserManagement = () => {
  const [users, setUsers] = useState(mockUserData);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showModelAccess, setShowModelAccess] = useState(null);
  const [availableModels, setAvailableModels] = useState({
    selfDeployed: [],
    saasModels: []
  });
  // Initialize available models
  useEffect(() => {
    // Get self-deployed models
    const selfDeployed = mockModelData.selfDeployed.map(model => ({
      id: model.id,
      name: model.name,
      type: 'self',
      family: model.family
    }));
    // Get SaaS models
    const saasModels = [];
    mockModelData.saasProviders.forEach(provider => {
      provider.models.forEach((model, index) => {
        saasModels.push({
          id: `${provider.name.toLowerCase()}-${index}`,
          name: model.name,
          provider: provider.name,
          type: 'saas'
        });
      });
    });
    setAvailableModels({
      selfDeployed,
      saasModels
    });
  }, []);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    email: '',
    role: 'view',
    allowedModel: '' // Changed from allowedModels array to allowedModel string
  });
  const handleAddUser = () => {
    if (newUser.username && newUser.password && newUser.email) {
      const newId = Math.max(...users.map(u => u.id)) + 1;
      setUsers([...users, {
        id: newId,
        username: newUser.username,
        password: newUser.password,
        email: newUser.email,
        role: newUser.role,
        lastActive: 'Never',
        resetRequired: true,
        apiKey: newUser.role === 'api' ? `mem_${Math.random().toString(36).substring(2, 15)}` : null,
        apiCalls: 0,
        allowedModel: newUser.role === 'api' ? newUser.allowedModel : '' // Single model ID
      }]);
      setNewUser({
        username: '',
        password: '',
        email: '',
        role: 'view',
        allowedModel: ''
      });
      setShowAddUser(false);
    }
  };
  const handleDeleteUser = id => {
    setUsers(users.filter(user => user.id !== id));
  };
  const handleResetPassword = id => {
    setUsers(users.map(user => user.id === id ? {
      ...user,
      resetRequired: true
    } : user));
  };
  const handleRegenerateApiKey = id => {
    setUsers(users.map(user => user.id === id ? {
      ...user,
      apiKey: `mem_${Math.random().toString(36).substring(2, 15)}`
    } : user));
  };
  const handleModelChange = modelId => {
    setNewUser({
      ...newUser,
      allowedModel: modelId
    });
  };
  const getRoleIcon = role => {
    switch (role) {
      case 'admin':
        return <UserIcon size={16} className="text-red-500" data-id="element-472" />;
      case 'view':
        return <EyeIcon size={16} className="text-blue-500" data-id="element-473" />;
      case 'api':
        return <CodeIcon size={16} className="text-green-500" data-id="element-474" />;
      default:
        return <UserIcon size={16} data-id="element-475" />;
    }
  };
  const getRoleBadge = role => {
    switch (role) {
      case 'admin':
        return <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded" data-id="element-476">
            Admin
          </span>;
      case 'view':
        return <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded" data-id="element-477">
            View Only
          </span>;
      case 'api':
        return <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded" data-id="element-478">
            API User
          </span>;
      default:
        return null;
    }
  };
  // Get model name by ID
  const getModelNameById = modelId => {
    if (!modelId) return 'None';
    // Check if it's a self-deployed model
    if (modelId.startsWith('self:')) {
      const id = modelId.split(':')[1];
      const model = availableModels.selfDeployed.find(m => `self:${m.id}` === modelId);
      return model ? model.name : 'Unknown Model';
    }
    // Check if it's a SaaS model
    if (modelId.startsWith('saas:')) {
      const id = modelId.split(':')[1];
      const model = availableModels.saasModels.find(m => `saas:${m.id}` === modelId);
      return model ? model.name : 'Unknown Model';
    }
    return 'Unknown Model';
  };
  // Modal for managing model access
  const ModelAccessModal = ({
    user,
    onSave,
    onCancel
  }) => {
    const [selectedModel, setSelectedModel] = useState(user.allowedModel || '');
    const handleSave = () => {
      setUsers(users.map(u => u.id === user.id ? {
        ...u,
        allowedModel: selectedModel
      } : u));
      onSave();
    };
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-479">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-480">
          <h2 className="text-xl font-semibold mb-4 flex items-center" data-id="element-481">
            <ServerIcon size={20} className="mr-2 text-gray-500" data-id="element-482" />
            Model Access for {user.username}
          </h2>
          <div className="space-y-6" data-id="element-483">
            <p className="text-sm text-gray-600" data-id="element-484">
              Select one model that this API user can access:
            </p>
            {/* Self-deployed Models */}
            <div data-id="element-485">
              <h3 className="text-lg font-medium mb-2" data-id="element-486">Self-deployed Models</h3>
              <div className="space-y-2" data-id="element-487">
                {availableModels.selfDeployed.map(model => <div key={`self:${model.id}`} className="flex items-center space-x-2 p-2 border rounded" data-id="element-488">
                    <input type="radio" id={`self:${model.id}`} name="modelAccess" checked={selectedModel === `self:${model.id}`} onChange={() => setSelectedModel(`self:${model.id}`)} className="h-4 w-4 text-blue-600" data-id="element-489" />
                    <label htmlFor={`self:${model.id}`} className="flex-1" data-id="element-490">
                      <div className="text-sm font-medium" data-id="element-491">{model.name}</div>
                      <div className="text-xs text-gray-500" data-id="element-492">
                        {model.family}
                      </div>
                    </label>
                  </div>)}
              </div>
            </div>
            {/* SaaS Models */}
            <div data-id="element-493">
              <h3 className="text-lg font-medium mb-2" data-id="element-494">SaaS Models</h3>
              <div className="space-y-2" data-id="element-495">
                {availableModels.saasModels.map(model => <div key={`saas:${model.id}`} className="flex items-center space-x-2 p-2 border rounded" data-id="element-496">
                    <input type="radio" id={`saas:${model.id}`} name="modelAccess" checked={selectedModel === `saas:${model.id}`} onChange={() => setSelectedModel(`saas:${model.id}`)} className="h-4 w-4 text-blue-600" data-id="element-497" />
                    <label htmlFor={`saas:${model.id}`} className="flex-1" data-id="element-498">
                      <div className="text-sm font-medium" data-id="element-499">{model.name}</div>
                      <div className="text-xs text-gray-500" data-id="element-500">
                        {model.provider}
                      </div>
                    </label>
                  </div>)}
              </div>
            </div>
            {/* No model option */}
            <div className="flex items-center space-x-2 p-2 border rounded" data-id="element-501">
              <input type="radio" id="no-model" name="modelAccess" checked={selectedModel === ''} onChange={() => setSelectedModel('')} className="h-4 w-4 text-blue-600" data-id="element-502" />
              <label htmlFor="no-model" className="flex-1" data-id="element-503">
                <div className="text-sm font-medium" data-id="element-504">No Access</div>
                <div className="text-xs text-gray-500" data-id="element-505">
                  User cannot access any models
                </div>
              </label>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t" data-id="element-506">
            <button onClick={onCancel} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" data-id="element-507">
              Cancel
            </button>
            <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-508">
              Save Changes
            </button>
          </div>
        </div>
      </div>;
  };
  const handleSaveModelAccess = () => {
    setShowModelAccess(null); // Close the modal after saving
  };
  return <div className="space-y-6" data-id="element-509">
      <div className="flex items-center justify-between" data-id="element-510">
        <h1 className="text-2xl font-bold text-gray-800" data-id="element-511">User Management</h1>
        <button onClick={() => setShowAddUser(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center" data-id="element-512">
          <PlusIcon size={16} className="mr-2" data-id="element-513" />
          Add User
        </button>
      </div>

      {/* Add User Form */}
      {showAddUser && <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200" data-id="element-514">
          <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-515">
            Add New User
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-516">
            <div data-id="element-517">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-518">
                Username
              </label>
              <input type="text" value={newUser.username} onChange={e => setNewUser({
            ...newUser,
            username: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-519" />
            </div>
            <div data-id="element-520">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-521">
                Password
              </label>
              <input type="password" value={newUser.password} onChange={e => setNewUser({
            ...newUser,
            password: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-522" />
            </div>
            <div data-id="element-523">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-524">
                Email
              </label>
              <input type="email" value={newUser.email} onChange={e => setNewUser({
            ...newUser,
            email: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-525" />
            </div>
            <div data-id="element-526">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-527">
                Role
              </label>
              <select value={newUser.role} onChange={e => setNewUser({
            ...newUser,
            role: e.target.value,
            // Reset allowed model if not an API user
            allowedModel: e.target.value === 'api' ? newUser.allowedModel : ''
          })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-528">
                <option value="admin" data-id="element-529">Admin</option>
                <option value="view" data-id="element-530">View Only</option>
                <option value="api" data-id="element-531">API User</option>
              </select>
            </div>
          </div>

          {/* Model Access Section for API Users */}
          {newUser.role === 'api' && <div className="mt-4 mb-4 border-t pt-4" data-id="element-532">
              <h3 className="text-md font-medium text-gray-800 mb-3" data-id="element-533">
                Model Access
              </h3>
              <p className="text-sm text-gray-600 mb-3" data-id="element-534">
                Select one model that this API user can access:
              </p>
              <div className="space-y-4" data-id="element-535">
                {/* Self-deployed Models */}
                <div data-id="element-536">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-537">
                    Self-deployed Models
                  </h4>
                  <div className="space-y-2" data-id="element-538">
                    {availableModels.selfDeployed.map(model => <div key={`self:${model.id}`} className="flex items-center space-x-2 p-2 border rounded" data-id="element-539">
                        <input type="radio" id={`new-self:${model.id}`} name="newUserModelAccess" checked={newUser.allowedModel === `self:${model.id}`} onChange={() => handleModelChange(`self:${model.id}`)} className="h-4 w-4 text-blue-600" data-id="element-540" />
                        <label htmlFor={`new-self:${model.id}`} className="flex-1" data-id="element-541">
                          <div className="text-sm font-medium" data-id="element-542">
                            {model.name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-543">
                            {model.family}
                          </div>
                        </label>
                      </div>)}
                  </div>
                </div>
                {/* SaaS Models */}
                <div data-id="element-544">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-545">
                    SaaS Models
                  </h4>
                  <div className="space-y-2" data-id="element-546">
                    {availableModels.saasModels.map(model => <div key={`saas:${model.id}`} className="flex items-center space-x-2 p-2 border rounded" data-id="element-547">
                        <input type="radio" id={`new-saas:${model.id}`} name="newUserModelAccess" checked={newUser.allowedModel === `saas:${model.id}`} onChange={() => handleModelChange(`saas:${model.id}`)} className="h-4 w-4 text-blue-600" data-id="element-548" />
                        <label htmlFor={`new-saas:${model.id}`} className="flex-1" data-id="element-549">
                          <div className="text-sm font-medium" data-id="element-550">
                            {model.name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-551">
                            {model.provider}
                          </div>
                        </label>
                      </div>)}
                  </div>
                </div>
                {/* No model option */}
                <div className="flex items-center space-x-2 p-2 border rounded" data-id="element-552">
                  <input type="radio" id="new-no-model" name="newUserModelAccess" checked={newUser.allowedModel === ''} onChange={() => handleModelChange('')} className="h-4 w-4 text-blue-600" data-id="element-553" />
                  <label htmlFor="new-no-model" className="flex-1" data-id="element-554">
                    <div className="text-sm font-medium" data-id="element-555">No Access</div>
                    <div className="text-xs text-gray-500" data-id="element-556">
                      User cannot access any models
                    </div>
                  </label>
                </div>
              </div>
            </div>}

          <div className="flex justify-end space-x-3" data-id="element-557">
            <button onClick={() => setShowAddUser(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" data-id="element-558">
              Cancel
            </button>
            <button onClick={handleAddUser} disabled={!newUser.username || !newUser.password || !newUser.email} className={`px-4 py-2 rounded-md transition-colors ${newUser.username && newUser.password && newUser.email ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`} data-id="element-559">
              Add User
            </button>
          </div>
        </div>}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-560">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center" data-id="element-561">
          <UsersIcon size={20} className="mr-2 text-gray-500" data-id="element-562" />
          <h2 className="text-lg font-medium text-gray-800" data-id="element-563">User Accounts</h2>
        </div>
        <div className="overflow-x-auto" data-id="element-564">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-565">
            <thead className="bg-gray-50" data-id="element-566">
              <tr data-id="element-567">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-568">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-569">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-570">
                  Last Active
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-571">
                  API Key
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-572">
                  API Calls
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-573">
                  Model Access
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-574">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-575">
              {users.map(user => <tr key={user.id} data-id="element-576">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-577">
                    <div className="flex items-center" data-id="element-578">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-100 rounded-full" data-id="element-579">
                        {getRoleIcon(user.role)}
                      </div>
                      <div className="ml-4" data-id="element-580">
                        <div className="text-sm font-medium text-gray-900" data-id="element-581">
                          {user.username}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-582">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-583">
                    {getRoleBadge(user.role)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-584">
                    {user.lastActive}
                    {user.resetRequired && <span className="ml-2 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded" data-id="element-585">
                        Reset Required
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-586">
                    {user.role === 'api' ? <div className="flex items-center space-x-2" data-id="element-587">
                        <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono" data-id="element-588">
                          {user.apiKey.substring(0, 8)}...
                        </code>
                        <button onClick={() => handleRegenerateApiKey(user.id)} className="text-gray-400 hover:text-blue-500" title="Regenerate API Key" data-id="element-589">
                          <RefreshCwIcon size={14} data-id="element-590" />
                        </button>
                      </div> : <span className="text-gray-400" data-id="element-591">—</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-592">
                    {user.role === 'api' ? user.apiCalls.toLocaleString() : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-593">
                    {user.role === 'api' ? <div className="flex items-center" data-id="element-594">
                        <span className="text-sm text-gray-700 mr-2" data-id="element-595">
                          {user.allowedModel ? getModelNameById(user.allowedModel) : 'No model assigned'}
                        </span>
                        <button onClick={() => setShowModelAccess(user)} className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 border border-blue-200 rounded" data-id="element-596">
                          Change
                        </button>
                      </div> : <span className="text-gray-400" data-id="element-597">—</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-598">
                    <div className="flex space-x-2" data-id="element-599">
                      <button onClick={() => handleResetPassword(user.id)} className="text-blue-600 hover:text-blue-800" title="Reset Password" data-id="element-600">
                        <KeyIcon size={16} data-id="element-601" />
                      </button>
                      <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:text-red-800" title="Delete User" data-id="element-602">
                        <TrashIcon size={16} data-id="element-603" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Model Access Modal */}
      {showModelAccess && <ModelAccessModal user={showModelAccess} onSave={handleSaveModelAccess} onCancel={() => setShowModelAccess(null)} data-id="element-604" />}
    </div>;
};
export default UserManagement;